/**
 * ContractCreateModal.jsx
 * 1-Klick Vertragsgenerierung aus einem eingereichten Personalbogen.
 * Daten werden automatisch übernommen, können aber vor der Generierung
 * noch angepasst werden (Position, Eintrittsdatum, Vergütung, Stunden, Urlaub).
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, FileText, Loader2, ChevronRight, User, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// YYYY-MM-DD → YYYY-MM-DD (passthrough) or DD.MM.YYYY → YYYY-MM-DD
function toDateInput(val) {
  if (!val) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(val)) {
    const [d, m, y] = val.split('.');
    return `${y}-${m}-${d}`;
  }
  return val;
}

export function ContractCreateModal({ open, onClose, bogen, templates, headers, onSuccess }) {
  const pe = bogen?.personal || {};
  const ag = bogen?.arbeitgeber || {};
  const be = bogen?.beschaeftigung || {};

  const displayName = bogen?.mitarbeiter_name ||
    `${pe.vorname || ''} ${pe.familienname || ''}`.trim() ||
    ag.berufsbezeichnung || 'Mitarbeiter';

  const isWerkstudent = ag.arbeitszeit === 'werkstudent';
  const isMinijob = ag.arbeitszeit === 'minijob';

  // Vergütung aus dem Personalbogen ableiten – Lohn liegt dort in stundenlohn/monatslohn (+lohnart),
  // NICHT immer im Anzeigefeld vergutung. Damit wird der Wert automatisch in den Vertrag übernommen.
  const deriveVergutung = (a) => {
    if (!a) return '';
    if (a.vergutung) return a.vergutung;
    if (a.lohnart === 'monatslohn' && a.monatslohn) return `${a.monatslohn} €/Monat`;
    if (a.stundenlohn) return `${a.stundenlohn} €/Std.`;
    if (a.monatslohn) return `${a.monatslohn} €/Monat`;
    return '';
  };

  const [templateId, setTemplateId] = useState('');
  const [overrides, setOverrides] = useState({
    job_bezeichnung: '',
    eintrittsdatum: '',
    vergutung: '',
    wochenstunden: '',
    urlaubstage: '20',
    arbeitstage_woche: '5',
    firma_name: '',
    firma_inhaber: '',
    firma_adresse: '',
    firma_plz_ort: '',
    lohnerhoehung_datum: '',
    neuer_stundenlohn: '',
    neuer_monatslohn: '',
    zusatz_info: '',
    // Werkstudent-spezifisch
    probezeit_dauer: '',
    befristung_bis: '',
    max_stunden_semester: '20',
    max_stunden_semesterferien: '40',
  });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [showBlanko, setShowBlanko] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewMode, setPreviewMode] = useState('rendered'); // 'rendered' | 'raw'

  // Bekannte Variable-Labels
  const VAR_LABELS = {
    vorname: 'Vorname', nachname: 'Nachname', an_strasse: 'Straße + Nr.',
    an_plz_ort: 'PLZ + Ort', an_geburtsdatum: 'Geburtsdatum',
    eintrittsdatum: 'Eintrittsdatum', job_bezeichnung: 'Tätigkeit / Position',
    vergutung: 'Vergütung', wochenstunden: 'Std./Woche',
    urlaubstage: 'Urlaubstage/Jahr', arbeitstage_woche: 'Arbeitstage/Woche',
    firma_name: 'Firmenname', firma_inhaber: 'Unterzeichner (AG)',
    firma_adresse: 'Firmenadresse', firma_plz_ort: 'Firmen-PLZ + Ort',
    standort: 'Standort', heutiges_datum: 'Datum (heute – auto)',
    probezeit_dauer: 'Probezeitdauer (z.B. 3 Monate)',
    befristung_bis: 'Befristet bis (Datum)',
    max_stunden_semester: 'Max. Std./Woche (Vorlesungszeit)',
    max_stunden_semesterferien: 'Max. Std./Woche (Semesterferien)',
    hochschule: 'Hochschule / Universität',
    matrikelnummer: 'Matrikelnummer',
    studiengang: 'Studiengang',
    lohnerhoehung_datum: 'Gültig ab (Lohnerhöhung)',
    neuer_stundenlohn: 'Neuer Stundenlohn (€/h)',
    neuer_monatslohn: 'Neuer Monatslohn (€/Monat)',
    zusatz_info: 'Freies Zusatzfeld',
  };
  // Variablen die AUTO befüllt werden (kein Blanko-Feld nötig)
  const AUTO_VARS = new Set(['heutiges_datum','urlaubstage_jahr','urlaubstage_berechnet','an_geburtsdatum']);

  // Passendes Template anhand Beschäftigungsart auto-auswählen
  const findBestTemplateId = (tpls) => {
    if (!tpls || tpls.length === 0) return '';
    const arbeitszeit = ag.arbeitszeit || '';
    if (arbeitszeit === 'werkstudent') {
      return tpls.find(t => t.contract_type === 'werkstudent')?.id || tpls[0].id;
    }
    if (arbeitszeit === 'minijob') {
      return tpls.find(t => t.contract_type === 'minijob')?.id || tpls[0].id;
    }
    return tpls.find(t => t.contract_type === 'vz')?.id || tpls[0].id;
  };

  // Template-Variablen aus Vorlagentext extrahieren
  const selectedTemplate = templates.find(t => t.id === templateId);
  const templateVars = selectedTemplate
    ? [...new Set([...selectedTemplate.template_text.matchAll(/\{\{(\w+)\}\}/g)].map(m => m[1]))]
        .filter(v => !AUTO_VARS.has(v))
    : [];

  // Daten aus Personalbogen laden + bei Template-Wechsel Variablen initialisieren
  useEffect(() => {
    if (!bogen || !open) return;
    setCreated(null);
    setShowBlanko(false);
    setShowPreview(false);
    setPreviewMode('rendered');
    setTemplateId(findBestTemplateId(templates));
    setOverrides({
      job_bezeichnung: ag.ausgeubte_taetigkeit || ag.berufsbezeichnung || '',
      eintrittsdatum: toDateInput(ag.eintrittsdatum || ''),
      vergutung: deriveVergutung(ag),
      wochenstunden: be.stunden_pro_woche || ag.stunden_pro_woche || '',
      urlaubstage: ag.urlaubstage || '20',
      arbeitstage_woche: be.arbeitstage_woche || ag.arbeitstage_woche || '5',
      firma_name: ag.firma || '',
      firma_inhaber: '',
      firma_adresse: '',
      firma_plz_ort: '',
      lohnerhoehung_datum: '',
      neuer_stundenlohn: '',
      neuer_monatslohn: '',
      zusatz_info: '',
      // Werkstudent-spezifisch (vorausgefüllt aus Personalbogen)
      probezeit_dauer: ag.probezeit_dauer || '',
      befristung_bis: toDateInput(ag.befristung_bis || ''),
      max_stunden_semester: ag.max_stunden_semester || '20',
      max_stunden_semesterferien: ag.max_stunden_semesterferien || '40',
    });
  }, [bogen, open]); // eslint-disable-line

  const set = (key, val) => setOverrides(o => ({ ...o, [key]: val }));

  // Anteiliger Urlaub bei < 5 Arbeitstage/Woche
  const arbeitstageNum = parseInt(overrides.arbeitstage_woche || '5', 10);
  const urlaubNum = parseInt(overrides.urlaubstage || '20', 10);
  const urlaubBerechnet = (arbeitstageNum < 5 && !isNaN(arbeitstageNum) && !isNaN(urlaubNum))
    ? Math.round(urlaubNum / 5 * arbeitstageNum)
    : urlaubNum;

  // Lohnbetrag aus Freitext ("14,00 €/Std." → "14,00") – spiegelt Backend _parse_wage_number
  const parseWage = (raw) => {
    if (!raw) return '';
    const m = String(raw).match(/(\d{1,4}[.,]?\d{0,2})/);
    return m ? m[1].replace('.', ',') : '';
  };
  // Stundenlohn wie im Backend build_variables: explizites Feld > Zahl aus (Override-)Vergütung > Vergütung-Text
  const computeStundenlohn = () => {
    const effVerg = overrides.vergutung || deriveVergutung(ag) || '';
    return ag.stundenlohn || be.stundenlohn || parseWage(effVerg) || effVerg || '';
  };

  // Lokales Template-Rendering für Vorschau (client-side, ohne Backend-Roundtrip)
  const renderPreview = () => {
    const tmpl = templates.find(t => t.id === templateId);
    if (!tmpl) return 'Keine Vorlage ausgewählt.';
    const today = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const fmtDate = (s) => {
      if (!s) return '–';
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) { const [y, m, d] = s.split('-'); return `${d}.${m}.${y}`; }
      return s;
    };
    const vars = {
      // Mitarbeiter
      vorname:          pe.vorname || overrides.vorname || '–',
      nachname:         pe.familienname || overrides.nachname || '–',
      an_strasse:       `${pe.strasse || ''} ${pe.hausnummer || ''}`.trim() || '–',
      an_plz_ort:       `${pe.plz || ''} ${pe.ort || ''}`.trim() || '–',
      an_geburtsdatum:  fmtDate(pe.geburtsdatum),
      // Vertrag
      eintrittsdatum:   fmtDate(overrides.eintrittsdatum) || fmtDate(ag.eintrittsdatum),
      job_bezeichnung:  overrides.job_bezeichnung || ag.ausgeubte_taetigkeit || ag.berufsbezeichnung || '–',
      taetigkeit:       overrides.job_bezeichnung || ag.ausgeubte_taetigkeit || '–',
      berufsbezeichnung:ag.berufsbezeichnung || overrides.job_bezeichnung || '–',
      vergutung:        overrides.vergutung || ag.vergutung || '–',
      wochenstunden:    overrides.wochenstunden || ag.stunden_pro_woche || '–',
      monatsstunden:    ag.stunden_pro_monat || '–',
      stundenlohn:      computeStundenlohn() || '–',
      monatslohn:       ag.monatslohn || parseWage(overrides.vergutung || deriveVergutung(ag)) || '–',
      urlaubstage:      overrides.urlaubstage || '20',
      urlaubstage_jahr: overrides.urlaubstage || '20',
      urlaubstage_berechnet: String(urlaubBerechnet),
      arbeitstage_woche:overrides.arbeitstage_woche || '5',
      heutiges_datum:   today,
      // Firma (aus Overrides oder Bogen)
      firma_name:       overrides.firma_name || ag.firma || '–',
      firma_inhaber:    overrides.firma_inhaber || '–',
      firma_adresse:    overrides.firma_adresse || '–',
      firma_plz_ort:    overrides.firma_plz_ort || '–',
      firma_unterzeichner: overrides.firma_inhaber || '–',
      standort:         ag.firma || overrides.firma_name || '–',
      // Werkstudent
      hochschule:       pe.hochschule || '–',
      matrikelnummer:   pe.matrikelnummer || '–',
      studiengang:      pe.studiengang || '–',
      probezeit_dauer:  overrides.probezeit_dauer || ag.probezeit_dauer || 'sechs Monate',
      befristung_bis:   fmtDate(overrides.befristung_bis) || fmtDate(ag.befristung_bis) || '–',
      max_stunden_semester: overrides.max_stunden_semester || '20',
      max_stunden_semesterferien: overrides.max_stunden_semesterferien || '40',
      // Zusatzvereinbarung
      lohnerhoehung_datum: overrides.lohnerhoehung_datum || today,
      neuer_stundenlohn: overrides.neuer_stundenlohn ? `${overrides.neuer_stundenlohn} €` : '–',
      neuer_monatslohn:  overrides.neuer_monatslohn ? `${overrides.neuer_monatslohn} €` : '–',
      zusatz_info:       overrides.zusatz_info || '',
    };
    // befristung_text: befristeter Satz ODER unbefristet
    const bef = vars.befristung_bis;
    vars.befristung_text = (bef && bef !== '–')
      ? `Das Arbeitsverhältnis wird auf bestimmte Zeit bis zum ${bef} geschlossen. Nach Ablauf endet das Arbeitsverhältnis automatisch, ohne dass es einer Kündigung bedarf.`
      : 'Das Arbeitsverhältnis wird auf unbestimmte Zeit geschlossen.';
    let text = tmpl.template_text;
    Object.entries(vars).forEach(([k, v]) => {
      text = text.replaceAll(`{{${k}}}`, v);
    });
    return text;
  };

  const handleCreate = async () => {
    if (!templateId) { toast.error('Bitte Vertragsvorlage auswählen'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/contracts/signing/create`, {
        personalbogen_id: bogen.id,
        template_id: templateId,
        override_variables: {
          job_bezeichnung: overrides.job_bezeichnung,
          eintrittsdatum: overrides.eintrittsdatum,   // Backend formatiert DD.MM.YYYY
          vergutung: overrides.vergutung,
          wochenstunden: overrides.wochenstunden,
          urlaubstage: overrides.urlaubstage,
          urlaubstage_jahr: overrides.urlaubstage,
          urlaubstage_berechnet: String(urlaubBerechnet),
          arbeitstage_woche: overrides.arbeitstage_woche,
          // Firmadaten – überschreiben Standort-Werte wenn ausgefüllt
          ...(overrides.firma_name    && { firma_name:    overrides.firma_name }),
          ...(overrides.firma_inhaber && { firma_inhaber: overrides.firma_inhaber }),
          ...(overrides.firma_adresse && { firma_adresse: overrides.firma_adresse }),
          ...(overrides.firma_plz_ort && { firma_plz_ort: overrides.firma_plz_ort }),
          // Werkstudent-spezifische Felder – immer übergeben wenn Werkstudent
          ...(isWerkstudent && {
            probezeit_dauer: overrides.probezeit_dauer || 'sechs Monate',
            befristung_bis: overrides.befristung_bis,   // Backend formatiert DD.MM.YYYY
            max_stunden_semester: overrides.max_stunden_semester || '20',
            max_stunden_semesterferien: overrides.max_stunden_semesterferien || '40',
          }),
          // Individuelle Angaben (Zusatzvereinbarungen)
          lohnerhoehung_datum: overrides.lohnerhoehung_datum || overrides.eintrittsdatum || new Date().toLocaleDateString('de-DE'),
          ...(overrides.neuer_stundenlohn && { neuer_stundenlohn: overrides.neuer_stundenlohn + ' €' }),
          ...(overrides.neuer_monatslohn  && { neuer_monatslohn:  overrides.neuer_monatslohn + ' €' }),
          ...(overrides.zusatz_info       && { zusatz_info:       overrides.zusatz_info }),
          // Alle Blanko-Template-Variablen übermitteln
          ...templateVars.reduce((acc, v) => {
            if (overrides[v] !== undefined && overrides[v] !== '') acc[v] = overrides[v];
            return acc;
          }, {}),
        },
      }, { headers });
      setCreated(res.data);
      toast.success('Vertrag wurde erfolgreich erstellt!');
      onSuccess?.(res.data, false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setSaving(false);
    }
  };

  if (!bogen) return null;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileText className="h-5 w-5 text-primary shrink-0" />
            Vertrag erstellen
          </DialogTitle>
        </DialogHeader>

        {!created ? (
          <div className="space-y-5 pb-1">

            {/* Mitarbeiter Info */}
            <div className="bg-muted/60 rounded-xl p-3.5 flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                <User className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-foreground text-sm">{displayName}</p>
                {(pe.strasse || pe.ort) && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {`${pe.strasse || ''} ${pe.hausnummer || ''}`.trim()}{pe.ort && `, ${pe.plz || ''} ${pe.ort}`.trim()}
                  </p>
                )}
                {pe.geburtsdatum && (
                  <p className="text-xs text-muted-foreground">Geb.: {pe.geburtsdatum}</p>
                )}
              </div>
            </div>

            {/* Firmadaten – editierbar für Vertrag */}
            <div className="space-y-3">
              <div className="flex items-center gap-1.5">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Arbeitgeber / Firma
                </p>
                <span className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-1.5 py-0.5 rounded">
                  Prüfen &amp; ergänzen
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs">Firmenname / Betriebsname</Label>
                  <Input
                    value={overrides.firma_name}
                    onChange={e => set('firma_name', e.target.value)}
                    placeholder="z.B. JET Tankstellen GmbH"
                    data-testid="cc-firma-name"
                  />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs">Inhaber / Betreiber</Label>
                  <Input
                    value={overrides.firma_inhaber}
                    onChange={e => set('firma_inhaber', e.target.value)}
                    placeholder="z.B. Ahmet Tombul"
                    data-testid="cc-firma-inhaber"
                  />
                </div>
                <div className="space-y-1.5 col-span-2 sm:col-span-1">
                  <Label className="text-xs">Straße + Hausnummer</Label>
                  <Input
                    value={overrides.firma_adresse}
                    onChange={e => set('firma_adresse', e.target.value)}
                    placeholder="z.B. Stuttgarter Str. 123"
                    data-testid="cc-firma-adresse"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">PLZ + Ort</Label>
                  <Input
                    value={overrides.firma_plz_ort}
                    onChange={e => set('firma_plz_ort', e.target.value)}
                    placeholder="z.B. 70736 Fellbach"
                    data-testid="cc-firma-plz-ort"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Tipp: Im <strong>Firmenprofil</strong> hinterlegte Daten werden automatisch übernommen. Hier nur ausfüllen wenn abweichend.
              </p>
            </div>

            {/* Vertragsdaten – bearbeitbar */}
            <div className="space-y-3">
              <div className="flex items-center gap-1.5">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Vertragsdaten prüfen &amp; anpassen
                </p>
                <div className="group relative">
                  <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-56 bg-popover border border-border rounded-lg p-2 text-xs text-muted-foreground shadow-lg invisible group-hover:visible z-50">
                    Werte werden automatisch aus dem Personalbogen übernommen – du kannst sie hier noch einmal anpassen, bevor der Vertrag eingefroren wird.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs">Position / Tätigkeit</Label>
                  <Input
                    value={overrides.job_bezeichnung}
                    onChange={e => set('job_bezeichnung', e.target.value)}
                    placeholder="z.B. Tankwart / Kassiererin"
                    data-testid="cc-job-bezeichnung"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Eintrittsdatum</Label>
                  <input
                    type="date"
                    value={overrides.eintrittsdatum}
                    onChange={e => set('eintrittsdatum', e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="cc-eintrittsdatum"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Brutto-Vergütung</Label>
                  <Input
                    value={overrides.vergutung}
                    onChange={e => set('vergutung', e.target.value)}
                    placeholder="14,50 €/Std."
                    data-testid="cc-vergutung"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Stunden / Woche</Label>
                  <Input
                    type="number"
                    value={overrides.wochenstunden}
                    onChange={e => set('wochenstunden', e.target.value)}
                    placeholder="40"
                    data-testid="cc-wochenstunden"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Arbeitstage / Woche</Label>
                  <select
                    value={overrides.arbeitstage_woche}
                    onChange={e => set('arbeitstage_woche', e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="cc-arbeitstage"
                  >
                    <option value="6">6 Tage</option>
                    <option value="5">5 Tage</option>
                    <option value="4">4 Tage</option>
                    <option value="3">3 Tage</option>
                    <option value="2">2 Tage</option>
                    <option value="1">1 Tag</option>
                  </select>
                </div>

                <div className="space-y-1.5 col-span-2 sm:col-span-1">
                  <Label className="text-xs">Urlaubstage / Jahr (Basis)</Label>
                  <Input
                    type="number"
                    value={overrides.urlaubstage}
                    onChange={e => set('urlaubstage', e.target.value)}
                    placeholder="20"
                    data-testid="cc-urlaubstage"
                  />
                </div>
              </div>

              {/* Anteiliger Urlaub – Hinweis */}
              {arbeitstageNum < 5 ? (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs flex items-start gap-2">
                  <span className="shrink-0 mt-0.5">📐</span>
                  <span className="text-amber-800 dark:text-amber-200">
                    <strong>Urlaub anteilig:</strong>{' '}
                    {overrides.urlaubstage} Tage ÷ 5 × {overrides.arbeitstage_woche} = <strong>{urlaubBerechnet} Tage</strong>
                    {' '}(wird als <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">{'{{urlaubstage_berechnet}}'}</code> im Vertrag eingesetzt)
                  </span>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Urlaub: <strong>{urlaubBerechnet} Tage/Jahr</strong> (5 Tage/Woche, kein Abzug)
                </p>
              )}
            </div>

            {/* ── Werkstudent-spezifische Felder ─────────────────────── */}
            {isWerkstudent && (
              <div className="space-y-3 rounded-xl border-2 border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-950/30 p-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">Werkstudent-Konditionen</span>
                  <span className="text-[10px] font-semibold bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300 px-1.5 py-0.5 rounded-full">Pflicht</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Probezeit-Dauer</Label>
                    <Input
                      value={overrides.probezeit_dauer}
                      onChange={e => set('probezeit_dauer', e.target.value)}
                      placeholder="z.B. 3 Monate"
                      data-testid="cc-probezeit-dauer"
                    />
                    <p className="text-[10px] text-muted-foreground">→ <code>{'{{probezeit_dauer}}'}</code></p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Befristet bis <span className="text-destructive">*</span></Label>
                    <input
                      type="date"
                      value={overrides.befristung_bis}
                      onChange={e => set('befristung_bis', e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid="cc-befristung-bis"
                    />
                    <p className="text-[10px] text-muted-foreground">→ <code>{'{{befristung_bis}}'}</code></p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Max. Std./Woche (Vorlesungszeit)</Label>
                    <Input
                      type="number"
                      value={overrides.max_stunden_semester}
                      onChange={e => set('max_stunden_semester', e.target.value)}
                      placeholder="20"
                      data-testid="cc-max-stunden-semester"
                    />
                    <p className="text-[10px] text-muted-foreground">→ <code>{'{{max_stunden_semester}}'}</code></p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Max. Std./Woche (Semesterferien)</Label>
                    <Input
                      type="number"
                      value={overrides.max_stunden_semesterferien}
                      onChange={e => set('max_stunden_semesterferien', e.target.value)}
                      placeholder="40"
                      data-testid="cc-max-stunden-semesterferien"
                    />
                    <p className="text-[10px] text-muted-foreground">→ <code>{'{{max_stunden_semesterferien}}'}</code></p>
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Vertragsvorlage <span className="text-destructive">*</span></Label>
              {templates.length === 0 ? (
                <div className="border border-dashed rounded-lg p-4 text-center text-sm text-muted-foreground">
                  Keine Vorlagen vorhanden. Bitte erst im Tab{' '}
                  <strong>Vertragsvorlagen</strong> Start-Templates einspielen.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {templates.map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTemplateId(t.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-all ${
                        templateId === t.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/40'
                      }`}
                      data-testid={`template-option-${t.id}`}
                    >
                      <FileText className={`h-4 w-4 shrink-0 ${templateId === t.id ? 'text-primary' : 'text-muted-foreground'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${templateId === t.id ? 'text-primary' : 'text-foreground'}`}>
                          {t.name}
                        </p>
                        {t.contract_type && (
                          <p className="text-xs text-muted-foreground capitalize">{t.contract_type}</p>
                        )}
                      </div>
                      {templateId === t.id && (
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ── BLANKO-VERTRAG: Alle Variablen der gewählten Vorlage ─── */}
            {templateId && templateVars.length > 0 && (
              <div className="space-y-3 rounded-xl border-2 border-primary/20 bg-primary/5 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-primary">Vertrags-Daten bearbeiten</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Alle Felder wurden aus dem Personalbogen vorausgefüllt. Bitte prüfen und ggf. anpassen.
                    </p>
                  </div>
                  <button type="button" onClick={() => setShowBlanko(v => !v)}
                    className="text-xs text-primary underline shrink-0">
                    {showBlanko ? 'Weniger' : 'Alle anzeigen'}
                  </button>
                </div>

                {/* Immer sichtbar: die wichtigsten Felder */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {['job_bezeichnung','eintrittsdatum','vergutung','wochenstunden','urlaubstage'].map(key => (
                    templateVars.includes(key) ? (
                      <div key={key} className="space-y-1">
                        <Label className="text-xs">{VAR_LABELS[key] || key}</Label>
                        <Input
                          type={key === 'eintrittsdatum' ? 'date' : 'text'}
                          value={key === 'eintrittsdatum' ? (overrides[key] ? toDateInput(overrides[key]) : '') : (overrides[key] || '')}
                          onChange={e => set(key, e.target.value)}
                          placeholder={VAR_LABELS[key] || key}
                          className="h-8 text-sm"
                        />
                      </div>
                    ) : null
                  ))}
                </div>

                {/* Alle Variablen (aufklappbar) */}
                {showBlanko && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t pt-3 mt-1">
                    {templateVars
                      .filter(v => !['job_bezeichnung','eintrittsdatum','vergutung','wochenstunden','urlaubstage'].includes(v))
                      .map(varName => (
                        <div key={varName} className="space-y-1">
                          <Label className="text-xs">{VAR_LABELS[varName] || varName}</Label>
                          <Input
                            type={varName.includes('datum') ? 'date' : 'text'}
                            value={overrides[varName] || ''}
                            onChange={e => set(varName, e.target.value)}
                            placeholder={VAR_LABELS[varName] || `{{${varName}}}`}
                            className="h-8 text-sm"
                          />
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Individuelle Angaben (Zusatzvereinbarungen) ─────────── */}
            <div className="space-y-3">
              <div className="flex items-center gap-1.5">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Individuelle Angaben
                </p>
                <span className="text-xs text-blue-600 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-1.5 py-0.5 rounded">
                  Für Zusatzvereinbarungen
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Nur ausfüllen wenn du eine Zusatzvereinbarung (z.B. Lohnerhöhung) erstellst.
                Diese Angaben überschreiben die Platzhalter in der Vorlage.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2 sm:col-span-1">
                  <Label className="text-xs">Gültig ab / Lohnerhöhung ab</Label>
                  <Input
                    type="date"
                    value={overrides.lohnerhoehung_datum}
                    onChange={e => set('lohnerhoehung_datum', e.target.value ? new Date(e.target.value).toLocaleDateString('de-DE') : '')}
                    data-testid="cc-lohnerhoehung-datum"
                  />
                  <p className="text-[10px] text-muted-foreground">→ Variable: <code>{'{{lohnerhoehung_datum}}'}</code></p>
                </div>
                <div className="space-y-1.5 col-span-2 sm:col-span-1">
                  <Label className="text-xs">Neuer Stundenlohn (€/h)</Label>
                  <Input
                    value={overrides.neuer_stundenlohn}
                    onChange={e => set('neuer_stundenlohn', e.target.value)}
                    placeholder="z.B. 14,50"
                    data-testid="cc-neuer-stundenlohn"
                  />
                  <p className="text-[10px] text-muted-foreground">→ Variable: <code>{'{{neuer_stundenlohn}}'}</code></p>
                </div>
                <div className="space-y-1.5 col-span-2 sm:col-span-1">
                  <Label className="text-xs">Neuer Monatslohn (€/Monat)</Label>
                  <Input
                    value={overrides.neuer_monatslohn}
                    onChange={e => set('neuer_monatslohn', e.target.value)}
                    placeholder="z.B. 1.800,00"
                    data-testid="cc-neuer-monatslohn"
                  />
                  <p className="text-[10px] text-muted-foreground">→ Variable: <code>{'{{neuer_monatslohn}}'}</code></p>
                </div>
                <div className="space-y-1.5 col-span-2 sm:col-span-1">
                  <Label className="text-xs">Freies Zusatzfeld</Label>
                  <Input
                    value={overrides.zusatz_info}
                    onChange={e => set('zusatz_info', e.target.value)}
                    placeholder="Beliebige Zusatzinfo"
                    data-testid="cc-zusatz-info"
                  />
                  <p className="text-[10px] text-muted-foreground">→ Variable: <code>{'{{zusatz_info}}'}</code></p>
                </div>
              </div>
            </div>

            {/* ── Vertragsvorschau ─────────────────────────────────── */}
            {templateId && (
              <div className="border border-border rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-muted/50 border-b border-border">
                  <button
                    type="button"
                    onClick={() => setShowPreview(v => !v)}
                    className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
                    data-testid="cc-preview-toggle"
                  >
                    <FileText className="h-4 w-4 text-primary" />
                    Vertrag vorschauen
                    <span className="text-xs text-muted-foreground ml-1">
                      {showPreview ? '▲' : '▼'}
                    </span>
                  </button>
                  {showPreview && (
                    <div className="flex rounded-lg border border-border overflow-hidden text-xs">
                      <button
                        type="button"
                        onClick={() => setPreviewMode('rendered')}
                        className={`px-3 py-1.5 font-medium transition-colors ${previewMode === 'rendered' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                        data-testid="cc-preview-mode-rendered"
                      >
                        Vorschau
                      </button>
                      <button
                        type="button"
                        onClick={() => setPreviewMode('raw')}
                        className={`px-3 py-1.5 font-medium transition-colors ${previewMode === 'raw' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                        data-testid="cc-preview-mode-raw"
                      >
                        Vorlage
                      </button>
                    </div>
                  )}
                </div>
                {showPreview && (
                  <>
                    {previewMode === 'rendered'
                      && (templates.find(t => t.id === templateId)?.template_text || '').includes('{{stundenlohn}}')
                      && !computeStundenlohn() && (
                      <div
                        className="mx-4 mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
                        data-testid="cc-stundenlohn-warning"
                      >
                        <span className="font-semibold">Hinweis:</span>
                        <span>Kein Stundenlohn hinterlegt. Bitte die Vergütung im Personalbogen eintragen oder oben im Feld „Vergütung" angeben – sonst erscheint im Vertrag „–".</span>
                      </div>
                    )}
                    <div
                      className="p-4 max-h-[45vh] overflow-y-auto bg-background text-xs leading-relaxed whitespace-pre-wrap font-mono"
                      data-testid="cc-preview-text"
                    >
                      {previewMode === 'rendered'
                        ? renderPreview()
                        : (templates.find(t => t.id === templateId)?.template_text || 'Keine Vorlage.')
                      }
                    </div>
                  </>
                )}
              </div>
            )}

            <Button
              className="w-full gap-2"
              onClick={handleCreate}
              disabled={saving || !templateId || templates.length === 0}
              data-testid="generate-contract-btn"
            >
              {saving
                ? <><Loader2 className="h-4 w-4 animate-spin" /> Wird erstellt...</>
                : <><FileText className="h-4 w-4" /> Vertrag generieren</>
              }
            </Button>
          </div>
        ) : (
          /* Erfolg */
          <div className="space-y-4 py-2">
            <div className="flex items-start gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
              <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-green-800 dark:text-green-300 text-sm">
                  Vertrag wurde erstellt!
                </p>
                <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                  Für <strong>{displayName}</strong> · Vorlage: „{created.template_name}"
                </p>
                <p className="text-xs text-green-600 dark:text-green-500 mt-0.5">
                  Status: Entwurf – Admin-Unterschrift noch ausstehend.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={onClose} data-testid="contract-close-btn">
                Schließen
              </Button>
              <Button
                className="flex-1 gap-1.5"
                onClick={() => { onSuccess?.(created, true); onClose(); }}
                data-testid="go-to-contract-btn"
              >
                Zum Vertrag <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
